import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

const foodImages = [
  { src: "/images/burger-sticker.webp", top: "18%", left: "15%", size: "w-16 h-16", delay: 0.1, rotate: 12 },
  { src: "/images/burger_sandwich_PNG4135.webp", top: "25%", right: "12%", size: "w-20 h-20", delay: 0.3, rotate: -15 },
  { src: "/images/frenchfry without bg.webp", bottom: "28%", left: "12%", size: "w-18 h-18", delay: 0.5, rotate: -8 },
  { src: "/images/sandwich without bg.webp", bottom: "22%", right: "15%", size: "w-22 h-22", delay: 0.7, rotate: 18 },
];

const SplashScreen = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const duration = 2000; // Total time to load
    const intervalTime = 40;
    const steps = duration / intervalTime;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const nextProgress = Math.min((currentStep / steps) * 100, 100);
      setProgress(nextProgress);
      if (currentStep >= steps) {
        clearInterval(timer);
        if (onComplete) {
          setTimeout(onComplete, 250); // slight delay after 100%
        }
      }
    }, intervalTime);

    return () => clearInterval(timer);
  }, [onComplete]);

  return (
    <div
      className="fixed inset-0 z-[99999] flex flex-col items-center justify-center overflow-hidden"
      style={{
        background: "radial-gradient(circle at 50% 40%, rgba(34, 197, 94, 0.12) 0%, #f8fafc 60%, #ffffff 100%)",
      }}
    >
      {/* Subtle Neon Radial Glow behind Logo */}
      <div
        className="absolute w-72 h-72 rounded-full pointer-events-none opacity-40 animate-pulse"
        style={{
          background: "radial-gradient(circle, rgba(34,197,94,0.3) 0%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />

      {/* Floating Food Elements */}
      {foodImages.map((food, i) => (
        <motion.img
          key={i}
          src={food.src}
          alt="decoration"
          initial={{ opacity: 0, scale: 0.5, y: 30, rotate: food.rotate }}
          animate={{
            opacity: 0.85,
            scale: 1,
            y: [0, -12, 0],
            rotate: [food.rotate, food.rotate + 3, food.rotate - 3, food.rotate],
          }}
          transition={{
            y: { duration: 6 + i, repeat: Infinity, ease: "easeInOut" },
            rotate: { duration: 8 + i, repeat: Infinity, ease: "easeInOut" },
            opacity: { duration: 0.6, delay: food.delay },
            scale: { duration: 0.6, delay: food.delay },
          }}
          className={`absolute ${food.size} object-contain pointer-events-none drop-shadow-lg hidden xs:block`}
          style={{
            top: food.top,
            left: food.left,
            right: food.right,
            bottom: food.bottom,
          }}
        />
      ))}

      {/* Brand logo container */}
      <div className="flex flex-col items-center relative z-10 max-w-[280px]">
        <motion.img
          src="/images/m logo.webp"
          alt="MarsTech Logo"
          initial={{ opacity: 0, scale: 0.88, filter: "blur(4px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="w-[48%] h-auto object-contain mb-8 drop-shadow-md"
        />

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 0.6 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="text-xs uppercase tracking-[0.25em] font-bold text-gray-500 mb-10 text-center"
        >
          Fine Dining Experience
        </motion.p>

        {/* Premium Progress Indicator */}
        <div className="w-48 h-1 rounded-full bg-gray-200/80 overflow-hidden relative border border-white/50 backdrop-blur-sm">
          <motion.div
            className="h-full bg-gradient-to-r from-green-500 via-[#10b981] to-green-600 rounded-full"
            style={{ width: `${progress}%` }}
            transition={{ ease: "linear" }}
          />
        </div>

        {/* Progress Text */}
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          className="text-[10px] tracking-widest font-semibold text-gray-500 mt-2.5"
        >
          {Math.round(progress)}%
        </motion.span>
      </div>
    </div>
  );
};

export default SplashScreen;
